import { Component } from '@theme/component';
import { preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Upper bound for a single add-to-cart request before the shopper gets an error instead of a spinner
const ADD_TO_CART_TIMEOUT = 20000;

// Upper bound for waiting on a variant section fetch when the variant can't be resolved from the markup
const VARIANT_RESOLVE_TIMEOUT = 10000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * Posts the add-to-cart form data and resolves with the parsed JSON response.
 * XHR rather than fetch: an app embed's fetch wrapper (the former Discount Ninja one) could hang cart requests forever after one fails.
 * @param {string} url
 * @param {FormData} body
 * @returns {Promise<{ status: number, data: any }>}
 */
function postCartAdd(url, body) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.timeout = ADD_TO_CART_TIMEOUT;
    xhr.onload = () => {
      try {
        resolve({ status: xhr.status, data: JSON.parse(xhr.responseText) });
      } catch {
        reject(new Error(`Unexpected add to cart response (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Add to cart network error'));
    xhr.onabort = () => reject(new Error('Add to cart request aborted'));
    xhr.ontimeout = () => reject(new Error('Add to cart request timed out'));
    xhr.send(body);
  });
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @property {HTMLElement} [klaviyoBisTrigger] - The Klaviyo Back-in-Stock trigger div.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Shows or clears the in-progress state while an add-to-cart request is pending: a spinner in
   * place of the label (add-to-cart-button.liquid) and the button marked disabled.
   *
   * `aria-disabled` rather than `disabled`: the real `disabled` state means "this variant can't be
   * added" (sold out / unavailable) and is what `#addToCart` checks before sending, so it must not
   * be borrowed for "a request is already running". Repeat submits are refused by
   * `ProductFormComponent#handleSubmit` while the request is pending.
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    const { addToCartButton } = this.refs;
    if (isLoading) {
      addToCartButton.setAttribute('aria-busy', 'true');
      addToCartButton.setAttribute('aria-disabled', 'true');
    } else {
      addToCartButton.removeAttribute('aria-busy');
      addToCartButton.removeAttribute('aria-disabled');
    }
  }

  /**
   * Plays the "added" feedback once the cart has confirmed the item.
   */
  showAddedFeedback() {
    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (animationEnabled && !this.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
    this.animateAddToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {Promise<boolean> | null} */
  #pendingAdd = null;

  /** @type {{ promise: Promise<void>, resolve: () => void, reject: (error: Error) => void, timer: number } | null} */
  #pendingVariant = null;

  /** @type {number | null} Option index of the latest selection not yet confirmed by a variant:update */
  #unconfirmedOptionIndex = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdateFailed, this.#onVariantUpdateFailed, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.pdpSectionFetchStart, this.#onSectionFetchStart, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#settleVariant(new Error('Product form disconnected'));
  }

  /**
   * The in-flight add-to-cart request for this form, resolving to whether the item was added.
   * @returns {Promise<boolean> | null}
   */
  get addToCartPromise() {
    return this.#pendingAdd;
  }

  /** @returns {AddToCartComponent[]} */
  get #addToCartContainers() {
    return /** @type {AddToCartComponent[]} */ (Array.from(this.querySelectorAll('add-to-cart-component')));
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * A page restored from the back/forward cache keeps its JS state, including a request that the
   * browser cancelled when the page was frozen, so release anything still marked as in progress.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    this.#pendingAdd = null;
    this.#setLoading(false);
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    // One request per form at a time: covers double taps, sticky bar + main button, and Enter key repeats.
    if (this.#pendingAdd) return;

    // Parts of the form that check their own fields first (the gift card recipient form,
    // assets/gift-card-recipient-form.js). The form is `novalidate`, so this is where an incomplete
    // form is stopped: its errors show inline and nothing is sent - no request, no cart drawer.
    for (const element of this.querySelectorAll('form [data-validate-before-add]')) {
      const validator = /** @type {{ validateForSubmit?: () => boolean }} */ (/** @type {unknown} */ (element));
      if (typeof validator.validateForSubmit === 'function' && !validator.validateForSubmit()) return;
    }

    const submitter = /** @type {SubmitEvent} */ (event).submitter;
    const isPuppet = submitter instanceof HTMLElement && submitter.dataset.puppet === 'true';
    const feedbackContainer = isPuppet
      ? null
      : /** @type {AddToCartComponent | null} */ (
          (submitter instanceof HTMLElement && submitter.closest('add-to-cart-component')) ||
            this.#addToCartContainers[0] ||
            null
        );

    const pendingAdd = this.#addToCart(event, feedbackContainer)
      .catch((error) => {
        console.error(error);
        return false;
      })
      .finally(() => {
        if (submitter instanceof HTMLElement && isPuppet) submitter.dataset.puppet = 'false';
        if (this.#pendingAdd !== pendingAdd) return;
        this.#pendingAdd = null;
        this.#setLoading(false);
      });

    this.#pendingAdd = pendingAdd;
  }

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    this.toggleAttribute('aria-busy', isLoading);
    for (const container of this.#addToCartContainers) {
      container.setLoading(isLoading);
    }

    // Add buttons not wrapped in an add-to-cart-component (the quick add drawer's "Add to cart",
    // the cart drawer's "+ Add" on recommendation cards) get the same busy state. `aria-busy="true"`
    // explicitly - their spinner styles match that value, not an empty attribute.
    for (const button of this.querySelectorAll('button[type="submit"][name="add"]')) {
      if (button.closest('add-to-cart-component')) continue;
      if (isLoading) {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
      }
    }
  }

  /**
   * @param {Event} event
   * @param {AddToCartComponent | null} feedbackContainer - Where to play the success animation, if anywhere.
   * @returns {Promise<boolean>} Whether the item was added.
   */
  async #addToCart(event, feedbackContainer) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    this.#setLoading(true);

    if (this.#pendingVariant) {
      const section = this.closest('.shopify-section');
      try {
        await this.#pendingVariant.promise;
      } catch (error) {
        if (!this.isConnected) {
          // The colour swap replaced this form while the add was waiting: the new form owns the shopper's tap.
          const replacement = section?.querySelector('product-form-component:not(product-card product-form-component)');
          if (replacement && replacement !== this) replacement.querySelector('form')?.requestSubmit();
          return false;
        }
        console.error(error);
        this.#showError(Theme.translations.add_to_cart_error);
        return false;
      }
    }

    const allAddToCartContainers = this.#addToCartContainers;
    const variantInput = this.refs.variantId;

    if (
      !variantInput?.value ||
      variantInput.disabled ||
      allAddToCartContainers.some((container) => container.refs.addToCartButton?.disabled)
    ) {
      return false;
    }

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        this.#showError(errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || ''));

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return false;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const addedQty = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

    try {
      const { data: response } = await postCartAdd(Theme.routes.cart_add_url, formData);

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
        );

        this.#showError(response.message);

        // Field errors (e.g. a gift card recipient's email, `errors: { email: [...] }`) reject the add
        // outright: nothing reached the cart, so there's no cart change to announce and the drawer
        // stays closed. The errors are shown by the form's own fields (CartErrorEvent above).
        const rejectedFields = response.errors !== null && typeof response.errors === 'object';

        // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
        // because our back-end still adds the max allowed amount to the cart.
        if (!rejectedFields) {
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: addedQty,
              productId: this.dataset.productId,
            })
          );
        }

        return false;
      }

      const id = formData.get('id');

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      if (!id) throw new Error('Form ID is required');

      // Add aria-live region to inform screen readers that the item was added
      // Get the added text from any add-to-cart button
      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      feedbackContainer?.showAddedFeedback();

      let b2bCard = this.closest('.product-card--b2b') || this.closest('.product-card[data-card-mode="b2b"]');
      if (!b2bCard) {
        const quickAddDialog = this.closest('quick-add-dialog');
        if (quickAddDialog && quickAddDialog.sourceQuickAdd) {
          b2bCard = quickAddDialog.sourceQuickAdd.closest('.product-card--b2b') || quickAddDialog.sourceQuickAdd.closest('.product-card[data-card-mode="b2b"]');
        }
      }
      const isB2b = !!b2bCard;

      // Dispatch CartAddEvent immediately so UI (cart drawer & header badge) updates without waiting for extra network fetch
      this.dispatchEvent(
        new CartAddEvent(undefined, id.toString(), {
          source: 'product-form-component',
          itemCount: addedQty,
          productId: this.dataset.productId,
          sections: response.sections,
          b2b: isB2b,
        })
      );

      // Update cart quantity for variant asynchronously in the background
      this.#fetchAndUpdateCartQuantity().then((cart) => {
        if (isB2b && cart) {
          this.#showB2bSuccessPopup(b2bCard, cart, id.toString(), addedQty);
        }
      });

      return true;
    } catch (error) {
      console.error(error);
      this.#showError(Theme.translations.add_to_cart_error);
      this.dispatchEvent(
        new CartErrorEvent(form.getAttribute('id') || '', Theme.translations.add_to_cart_error, undefined, undefined)
      );
      return false;
    } finally {
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Shows an inline error under the add to cart button and announces it.
   * @param {string} message
   */
  #showError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError || !message) return;

    addToCartTextError.classList.remove('hidden');

    // Reuse the text node if the user is spam-clicking
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }

    this.#setLiveRegionText(message);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #showB2bSuccessPopup(b2bCard, cart, variantId, qtyAdded) {
    let popupContainer = document.getElementById('b2b-toast-container');
    if (!popupContainer) {
      popupContainer = document.createElement('div');
      popupContainer.id = 'b2b-toast-container';
      document.body.appendChild(popupContainer);
    }

    const popup = document.createElement('div');
    popup.className = 'b2b-success-popup';
    popupContainer.appendChild(popup);

    const addedItem = cart?.items?.find(item => item.variant_id.toString() === variantId.toString() || item.id.toString() === variantId.toString());
    const itemTitle = addedItem ? addedItem.product_title : (b2bCard.querySelector('.h4')?.textContent || '');
    let variantTitle = '';
    if (addedItem && addedItem.variant_title && addedItem.variant_title !== 'Default Title') {
      variantTitle = `Size: ${addedItem.variant_title}`;
    }

    const img = b2bCard.querySelector('img');
    const imgSrc = addedItem?.image || (img ? img.src : '');
    const viewCartText = Theme.translations?.view_cart || 'View cart';

    popup.innerHTML = `
      <div class="b2b-success-popup__header">
        <img src="${imgSrc}" class="b2b-success-popup__image" alt="Product image">
        <div class="b2b-success-popup__info">
          <div class="b2b-success-popup__title">${itemTitle}</div>
          <div class="b2b-success-popup__variant">${variantTitle}</div>
        </div>
      </div>
      <div class="b2b-success-popup__footer">
        <span class="b2b-success-popup__message">${qtyAdded} added to cart</span>
        <button type="button" class="b2b-view-cart-btn">${viewCartText}</button>
      </div>
    `;

    const viewCartBtn = popup.querySelector('.b2b-view-cart-btn');
    if (viewCartBtn) {
      viewCartBtn.addEventListener('click', () => {
        document.querySelector('cart-drawer-component')?.open();
        popup.remove();
      });
    }

    setTimeout(() => {
      if (popup.parentNode) popup.remove();
    }, 5000);
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * Whether a variant event belongs to this form, not to another product's picker in the same section.
   * @param {Event} event
   * @param {string | undefined} productId
   * @returns {boolean}
   */
  #isOwnVariantEvent(event, productId) {
    if (productId && this.dataset.productId && productId !== this.dataset.productId) return false;

    const origin = event.target instanceof Element ? event.target : null;
    return !origin || origin.closest('product-card') === this.closest('product-card');
  }

  #startVariantResolution() {
    if (this.#pendingVariant) return;

    /** @type {() => void} */
    let resolve = () => {};
    /** @type {(error: Error) => void} */
    let reject = () => {};
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    // Nothing awaits this unless the shopper submits while it's pending.
    promise.catch(() => {});

    const timer = setTimeout(
      () => this.#settleVariant(new Error('Timed out waiting for the selected variant')),
      VARIANT_RESOLVE_TIMEOUT
    );

    this.#pendingVariant = { promise, resolve, reject, timer };
  }

  /** @param {Error} [error] */
  #settleVariant(error) {
    const pending = this.#pendingVariant;
    if (!pending) return;

    this.#pendingVariant = null;
    clearTimeout(pending.timer);

    if (error) {
      pending.reject(error);
    } else {
      pending.resolve();
    }
  }

  /**
   * Applies the selected option's variant right away so an add submitted before the section fetch
   * lands (or if it fails) still uses the variant the shopper can see selected.
   * @param {import('./events').VariantSelectedEvent} event
   */
  #onVariantSelected = (event) => {
    const selection = event.detail?.resource;
    if (!selection || !this.#isOwnVariantEvent(event, selection.productId)) return;

    const optionIndex = Number.isInteger(selection.optionIndex) ? selection.optionIndex : -1;
    // data-variant-id on another option was rendered against the previous value of an option whose
    // change hasn't been confirmed yet, so it can't be trusted until the section update arrives.
    const dependsOnUnconfirmedOption =
      this.#unconfirmedOptionIndex !== null && (optionIndex === -1 || optionIndex !== this.#unconfirmedOptionIndex);
    this.#unconfirmedOptionIndex = optionIndex;

    if (selection.variantId === undefined || selection.loadsNewProduct || dependsOnUnconfirmedOption) {
      this.#startVariantResolution();
      return;
    }

    this.#settleVariant();

    const { variantId } = this.refs;
    variantId.value = selection.variantId;

    const canAdd = Boolean(selection.variantId) && selection.available !== false;
    for (const container of this.#addToCartContainers) {
      if (canAdd) {
        container.enable();
      } else {
        container.disable();
      }
    }
  };

  /**
   * A PDP colour swatch is a different product: its image swaps instantly while this form still holds
   * the previous product until the section is replaced, so an add in that window must wait.
   * @param {CustomEvent<{ source: string }>} event
   */
  #onSectionFetchStart = (event) => {
    if (event.detail?.source !== 'product-pdp-stitching') return;
    if (this.closest('product-card, dialog') || !this.closest('.product-information, [data-testid="product-information"]')) return;

    this.#startVariantResolution();
  };

  /** @param {CustomEvent<{ data: { productId: string } }>} event */
  #onVariantUpdateFailed = (event) => {
    if (!this.#isOwnVariantEvent(event, event.detail?.data?.productId)) return;

    this.#unconfirmedOptionIndex = null;
    this.#settleVariant(new Error('The selected variant could not be loaded'));
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#unconfirmedOptionIndex = null;
    this.#settleVariant();

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Klaviyo's Back-in-Stock trigger div sits alongside (not inside)
    // [ref="addToCartButton"], so morphing the button above never touches
    // its data-variant/data-available/data-discontinued attributes or its
    // conditional klaviyo-bis-trigger class. Left stale, Klaviyo keeps
    // treating the trigger as the page-load variant, so "Notify me" opens
    // the wrong popup (or none) after a client-side variant switch until
    // the page is reloaded. Copy only the wrapper's own attributes here
    // (not a morph() of its subtree) - its one child is the same
    // [ref="addToCartButton"] node already morphed above, and running a
    // second, wider morph pass over that already-updated button would
    // re-diff it against the now-partially-consumed parsed response
    // (morph() moves reused nodes rather than cloning them), stripping
    // content the first pass just added - the exact bug this file's
    // sibling components hit for the same reason.
    const currentBisTrigger = currentAddToCartButtonContainer.refs.klaviyoBisTrigger;
    const newBisTrigger = event.detail.data.html.querySelector('[ref="klaviyoBisTrigger"]');
    if (currentBisTrigger && newBisTrigger) {
      currentBisTrigger.className = newBisTrigger.className;
      for (const attr of ['data-product', 'data-variant', 'data-available', 'data-discontinued']) {
        const value = newBisTrigger.getAttribute(attr);
        value == null ? currentBisTrigger.removeAttribute(attr) : currentBisTrigger.setAttribute(attr, value);
      }
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
